import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';

//--- Styled Components ---//

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 30px;
  background-color: #ffffff;
  border-bottom: 1px solid #ddd;
  padding: 0 10px 0 0;
  flex-shrink: 0;
`;

const TabsContainer = styled.div`
  flex-grow: 1;
  overflow: hidden;
  position: relative;
`;

const TabsList = styled.div`
  display: flex;
  transition: transform 0.3s ease-in-out;
  transform: translateX(${({ position }) => `-${position}px`});
`;

const Tab = styled.button`
  padding: 0 15px;
  cursor: pointer;
  border: 1px solid #ccc;
  border-bottom: none; 
  font-size: 13px;
  margin-right: 2px;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  display: flex;
  align-items: center;

  flex-shrink: 0; /* Prevent tabs from shrinking in the flex container */
  min-width: 125px;  /* Set a minimum width for short names */
  max-width: 220px; /* Set a maximum width for long names */
  overflow: hidden; /* Hide the text that overflows */
  
  /* Styles for the ACTIVE tab */
  color: ${({ isActive }) => (isActive ? '#7A9F3F' : '#333')};
  font-weight: ${({ isActive }) => (isActive ? '600' : '500')};
  background-color: ${({ isActive }) => (isActive ? '#ffffff' : '#e9ecef')};
  
  /* Make active tab look connected */
  margin-bottom: ${({ isActive }) => (isActive ? '-1px' : '0')};
  height: 30px;
  
  &:hover {
    background-color: #ffffff;
  }
`;

const TabText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NavButtons = styled.div`
  display: flex;
  gap: 5px;
  margin-left: 5px;
`;

const ArrowButton = styled.button`
  border: 1px solid #ced4da;
  background-color: #fff;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:hover:not(:disabled) {
    background-color: #f1f1f1;
  }
`;

const WizardHeader = ({ pages, activePageId, onPageChange }) => {

  const [scrollPosition, setScrollPosition] = useState(0);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const tabsContainerRef = useRef(null);
  const tabsListRef = useRef(null);

  // Effect to detect overflow and update button states ---
  useEffect(() => {
    const checkOverflow = () => {
      const container = tabsContainerRef.current;
      const list = tabsListRef.current;

      if (container && list) {
        const hasOverflow = list.scrollWidth > container.clientWidth;
        setIsOverflowing(hasOverflow);

        if (hasOverflow) {
          // Round positions to avoid floating point inaccuracies
          const currentPos = Math.round(scrollPosition);
          const maxScroll = Math.round(list.scrollWidth - container.clientWidth);

          setCanScrollBack(currentPos > 0);
          setCanScrollNext(currentPos < maxScroll);
        } else {
          setCanScrollBack(false);
          setCanScrollNext(false);
        }
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [pages, scrollPosition]);

  const handleNext = () => {
    const container = tabsContainerRef.current;
    const list = tabsListRef.current;
    if (container && list) {
      const amountToScroll = container.clientWidth * 0.8; // Scroll by 80% of the visible width
      const maxScroll = list.scrollWidth - container.clientWidth;
      const newPosition = Math.min(scrollPosition + amountToScroll, maxScroll);
      setScrollPosition(newPosition);
    }
  };

  const handleBack = () => {
    const container = tabsContainerRef.current;
    if (container) {
      const amountToScroll = container.clientWidth * 0.8;
      const newPosition = Math.max(scrollPosition - amountToScroll, 0);
      setScrollPosition(newPosition);
    }
  };

  return (
    <HeaderWrapper>
      <TabsContainer ref={tabsContainerRef}>
        <TabsList ref={tabsListRef} position={scrollPosition}>
          {pages.map(page => (
            <Tab
              key={page.id}
              isActive={page.id === activePageId}
              onClick={() => onPageChange(page.id)}
              title={page.name}
            >
              <TabText>{page.name}</TabText>
            </Tab>
          ))}
        </TabsList>
      </TabsContainer>

      {/* Conditionally render the navigation buttons */}
      {isOverflowing && (
        <NavButtons>
          <ArrowButton onClick={handleBack} disabled={!canScrollBack}>{'<'}</ArrowButton>
          <ArrowButton onClick={handleNext} disabled={!canScrollNext}>{'>'}</ArrowButton>
        </NavButtons>
      )}
    </HeaderWrapper>
  );
};

export default WizardHeader;